import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import type { VampDay } from "../../../types/vampRates";
import { VAMP_EXCESSIVE_PCT, VAMP_SCORING_THRESHOLD, fmtInt, fmtPct, vampRatePct, type VampMonthContext } from "./vampStanding";

dayjs.extend(utc);

// The SVG renders at width 100% and scales to this viewBox, so x positions are in viewBox units
// and hover geometry is expressed in percent of CHART_W. Height is fixed, so y is in pixels.
const CHART_W = 1084;
const CHART_H = 206;
const TOP = 12;
const BASE = 176;
const PLOT = BASE - TOP;
const GUTTER_L = 44;
const GUTTER_R = 40;

// Frame geometry: fixed by the design, so it never depends on the data and is not rebuilt with it.
export const CHART_LAYOUT = {
  viewBox: `0 0 ${CHART_W} ${CHART_H}`,
  height: CHART_H,
  plotHeight: BASE,
  topY: TOP,
  baseY: BASE,
  plotSpan: PLOT,
  gutterL: GUTTER_L,
  axisX2: CHART_W - GUTTER_R,
  zeroTop: BASE - 7,
  xLabelTop: BASE + 6,
  labelPrevPct: (GUTTER_L / CHART_W) * 100,
  labelRightPct: (GUTTER_R / CHART_W) * 100,
} as const;
// Roughly one "12 Sep" label's width, so the today label yields to a month label it would overlap.
const LABEL_GAP_PCT = 5;

export interface VampDayPoint {
  date: string;
  month: string;
  dayNo: number;
  settled: number;
  tc40: number;
  tc15: number;
  cumSettled: number;
  cumTc40: number;
  cumTc15: number;
  mtdRate: number;
}

export function buildDayPoints(days: VampDay[]): VampDayPoint[] {
  const out: VampDayPoint[] = [];
  let month = "";
  let cumSettled = 0;
  let cumTc40 = 0;
  let cumTc15 = 0;
  for (const d of days) {
    const m = d.date.slice(0, 7);
    if (m !== month) {
      month = m;
      cumSettled = 0;
      cumTc40 = 0;
      cumTc15 = 0;
    }
    cumSettled += d.settled;
    cumTc40 += d.tc40;
    cumTc15 += d.tc15;
    out.push({
      date: d.date,
      month: m,
      dayNo: Number(d.date.slice(8, 10)),
      settled: d.settled,
      tc40: d.tc40,
      tc15: d.tc15,
      cumSettled,
      cumTc40,
      cumTc15,
      mtdRate: vampRatePct(cumTc40 + cumTc15, cumSettled),
    });
  }
  return out;
}

// Distinct headroom per axis: equal headroom would land both threshold lines on the same pixel row
// whenever neither is exceeded, which reads as one ambiguous line.
export const countDomain = (maxCount: number, showFloor: boolean): number =>
  Math.max(maxCount * 1.12, showFloor ? VAMP_SCORING_THRESHOLD / 0.82 : 0, 1);

export const rateDomain = (maxRate: number): number => Math.max(maxRate * 1.12, VAMP_EXCESSIVE_PCT / 0.6, 0.1);

export interface VampChartBar {
  date: string;
  day: string;
  x: number;
  yAll: number;
  hAll: number;
  y40: number;
  h40: number;
  hitLeftPct: number;
  hitWidthPct: number;
  centerPct: number;
  dotTop: number;
  valTop: number;
  mtd: string;
  events: string;
  eventsMtd: string;
  tc40: string;
  tc15: string;
  settled: string;
  cardOnLeft: boolean;
}

export interface VampChartModel {
  barWidth: number;
  boundaryX: number;
  todayX: number;
  futureW: number;
  showToday: boolean;
  // The last recorded day's date on the x axis, or null when it would sit on a month label.
  todayLabel: string | null;
  todayLabelPct: number;
  ratePath: string;
  showFloor: boolean;
  floorLabel: string;
  floorY: number;
  floorTop: number;
  rateY: number;
  rateTop: number;
  // The bar carrying the month-to-date marker, shown while nothing is hovered.
  restBar: VampChartBar | null;
  labelMonthPct: number;
  prevMonthLabel: string;
  monthLabel: string;
  lastDayLabel: string;
  bars: VampChartBar[];
}

const pathOf = (pts: { x: number; y: number }[]): string =>
  pts.map((q, i) => `${i === 0 ? "M" : "L"}${q.x.toFixed(1)} ${q.y.toFixed(1)}`).join(" ");

export function buildChartModel(days: VampDay[], ctx: VampMonthContext, showFloor: boolean): VampChartModel {
  const monthStart = dayjs.utc(`${ctx.month}-01`);
  const prevStart = monthStart.subtract(1, "month");
  const prevMonth = prevStart.format("YYYY-MM");
  const prevDays = prevStart.daysInMonth();
  const band = (CHART_W - GUTTER_L - GUTTER_R) / (prevDays + ctx.daysInMonth);
  const X = (slot: number) => GUTTER_L + (slot + 0.5) * band;
  const EDGE = (slot: number) => GUTTER_L + slot * band;
  const pct = (x: number) => (x / CHART_W) * 100;
  const barWidth = Math.max(6, Math.min(16, band * 0.62));

  const points = buildDayPoints(days);
  const slotOf = (p: VampDayPoint) => (p.month === prevMonth ? 0 : prevDays) + p.dayNo - 1;
  const currentPoints = points.filter((p) => p.month === ctx.month);

  const cDomain = countDomain(Math.max(0, ...points.map((p) => p.cumTc40 + p.cumTc15)), showFloor);
  const rDomain = rateDomain(Math.max(0, ...points.map((p) => p.mtdRate)));
  const CY = (v: number) => BASE - (v / cDomain) * PLOT;
  const RY = (v: number) => BASE - (v / rDomain) * PLOT;

  const bars: VampChartBar[] = points.map((p) => {
    const slot = slotOf(p);
    const hAll = ((p.cumTc40 + p.cumTc15) / cDomain) * PLOT;
    const h40 = (p.cumTc40 / cDomain) * PLOT;
    return {
      date: p.date,
      day: dayjs.utc(p.date).format("D MMM"),
      x: X(slot) - barWidth / 2,
      yAll: BASE - hAll,
      hAll,
      y40: BASE - h40,
      h40,
      hitLeftPct: pct(EDGE(slot)),
      hitWidthPct: pct(band),
      centerPct: pct(X(slot)),
      dotTop: RY(p.mtdRate) - 4,
      valTop: RY(p.mtdRate) - 20,
      mtd: fmtPct(p.mtdRate),
      events: fmtInt(p.tc40 + p.tc15),
      eventsMtd: fmtInt(p.cumTc40 + p.cumTc15),
      tc40: fmtInt(p.tc40),
      tc15: fmtInt(p.tc15),
      settled: fmtInt(p.settled),
      cardOnLeft: X(slot) > CHART_W * 0.52,
    };
  });

  const last = currentPoints[currentPoints.length - 1];
  const lastSlot = last ? slotOf(last) : prevDays - 1;
  const todayX = ctx.isCurrent ? EDGE(lastSlot + 1) : CHART_W - GUTTER_R;
  const labelMonthPct = pct(EDGE(prevDays));
  const todayLabelPct = pct(X(lastSlot));
  const clearOfMonthLabels =
    todayLabelPct - labelMonthPct > LABEL_GAP_PCT && CHART_LAYOUT.labelRightPct + LABEL_GAP_PCT < 100 - todayLabelPct;

  return {
    barWidth,
    boundaryX: EDGE(prevDays),
    todayX,
    futureW: CHART_W - GUTTER_R - todayX,
    showToday: ctx.isCurrent,
    todayLabel: last && ctx.isCurrent && clearOfMonthLabels ? dayjs.utc(last.date).format("D MMM") : null,
    todayLabelPct,
    ratePath: pathOf(points.map((p) => ({ x: X(slotOf(p)), y: RY(p.mtdRate) }))),
    showFloor,
    floorLabel: fmtInt(VAMP_SCORING_THRESHOLD),
    floorY: CY(VAMP_SCORING_THRESHOLD),
    floorTop: CY(VAMP_SCORING_THRESHOLD) - 7,
    rateY: RY(VAMP_EXCESSIVE_PCT),
    rateTop: RY(VAMP_EXCESSIVE_PCT) - 7,
    restBar: last && ctx.isCurrent ? bars[points.indexOf(last)] : null,
    labelMonthPct,
    prevMonthLabel: `1 ${prevStart.format("MMM")}`,
    monthLabel: `1 ${monthStart.format("MMM")}`,
    lastDayLabel: `${ctx.daysInMonth} ${monthStart.format("MMM")}`,
    bars,
  };
}
